import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import {
  Breadcrumb,
  Card,
  Descriptions,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import { useParams, useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { MONO_STYLE } from '@/components/common/MonoId'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { useClustersQuery } from '@/hooks/useClusters'

type ClusterResourcesDto = components['schemas']['ClusterResourcesDto']
type VmConfigurationDto = components['schemas']['VmConfigurationDto']
type AvailableStorageDto = components['schemas']['AvailableStorageDto']

export default function ClusterShowPage() {
  const { id: clusterId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'configurations'

  const clustersQuery = useClustersQuery()

  const resourcesQuery = useCustom<ClusterResourcesDto>({
    url: `${VODOPAD_ENDPOINT}/v1/clusters/${clusterId}/resources`,
    method: 'get',
    queryOptions: { enabled: Boolean(clusterId) },
  })

  const cluster = clustersQuery.data?.find((c) => c.id === clusterId)
  const resources = resourcesQuery.query.data?.data as
    | ClusterResourcesDto
    | undefined

  if (!clusterId) return null

  if (clustersQuery.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!cluster) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>Cluster not found</div>
    )
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/clusters">Clusters</Link> },
          { title: cluster.name },
        ]}
      />
      <h2 style={{ marginBottom: 24 }}>
        Cluster: <Typography.Text copyable>{cluster.name}</Typography.Text>
      </h2>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {cluster.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            <Typography.Text copyable>{cluster.name}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="DC ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {cluster.dcId}
            </Typography.Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {resourcesQuery.query.isLoading && <Spin />}
      {!resourcesQuery.query.isLoading && resources && (
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
          items={[
            {
              key: 'configurations',
              label: `Configurations (${resources.availableConfigurations?.length ?? 0})`,
              children: (
                <ConfigurationsTab
                  configurations={resources.availableConfigurations ?? []}
                />
              ),
            },
            {
              key: 'publicIps',
              label: 'Public IPs',
              children: (
                <PublicIpsTab ips={resources.availablePublicIps ?? {}} />
              ),
            },
            {
              key: 'storage',
              label: 'Storage',
              children: (
                <StorageTab storage={resources.availableStorage ?? {}} />
              ),
            },
          ]}
        />
      )}
    </div>
  )
}

function ConfigurationsTab({
  configurations,
}: {
  configurations: VmConfigurationDto[]
}) {
  return (
    <Table
      dataSource={configurations}
      rowKey="id"
      pagination={{ pageSize: 10, showTotal: (total) => `${total} items` }}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: 'Slug',
          dataIndex: 'slug',
          key: 'slug',
          render: (s: string) => <Tag>{s}</Tag>,
        },
        {
          title: 'vCPU',
          dataIndex: 'vcpu',
          key: 'vcpu',
          width: 80,
        },
        {
          title: 'RAM (GB)',
          dataIndex: 'ramGb',
          key: 'ramGb',
          width: 100,
        },
        {
          title: 'CPU Families',
          dataIndex: 'cpuFamilies',
          key: 'cpuFamilies',
          render: (families: string[]) =>
            families?.map((f) => <Tag key={f}>{f}</Tag>) ?? '—',
        },
        {
          title: 'Dedicated',
          dataIndex: 'dedicated',
          key: 'dedicated',
          width: 100,
          render: (d: boolean) => (d ? 'Yes' : 'No'),
        },
        {
          title: 'Tags',
          dataIndex: 'tags',
          key: 'tags',
          render: (tags: string[]) =>
            tags?.map((t) => (
              <Tag key={t} color="blue">
                {t}
              </Tag>
            )) ?? '—',
        },
      ]}
    />
  )
}

function PublicIpsTab({ ips }: { ips: Record<string, number> }) {
  const data = Object.entries(ips).map(([type, count]) => ({ type, count }))

  return (
    <Table
      dataSource={data}
      rowKey="type"
      pagination={false}
      columns={[
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Available', dataIndex: 'count', key: 'count' },
      ]}
    />
  )
}

function StorageTab({
  storage,
}: {
  storage: Record<string, AvailableStorageDto[]>
}) {
  const data = Object.entries(storage).flatMap(([category, items]) =>
    items.map((item, i) => ({
      key: `${category}-${i}`,
      category,
      storageType: item.storageType,
      volumeGb: item.volumeGb,
      replicated: item.replicated,
    })),
  )

  return (
    <Table
      dataSource={data}
      rowKey="key"
      pagination={false}
      columns={[
        { title: 'Category', dataIndex: 'category', key: 'category' },
        { title: 'Type', dataIndex: 'storageType', key: 'storageType' },
        { title: 'Volume (GB)', dataIndex: 'volumeGb', key: 'volumeGb' },
        {
          title: 'Replicated',
          dataIndex: 'replicated',
          key: 'replicated',
          render: (r: boolean) => (r ? 'Yes' : 'No'),
        },
      ]}
    />
  )
}
